import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import FilterBar, { type FilterGroup } from '@/components/feature/FilterBar';
import Pagination from '@/components/feature/Pagination';
import SortTabs from '@/components/feature/SortTabs';
import VideoCard from '@/components/feature/VideoCard';
import { categories, perspectives, regions, years } from '@/mocks/categories';
import { filterVideos, sortVideos, type SortKey } from '@/lib/videos';
import { usePageMeta } from '@/hooks/usePageMeta';

const PER_PAGE = 9;

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const categoryName = type ? decodeURIComponent(type) : '';
  const info = categories.find((item) => item.slug === categoryName);

  const [region, setRegion] = useState('');
  const [perspective, setPerspective] = useState('');
  const [year, setYear] = useState('');
  const [sort, setSort] = useState<SortKey>('latest');
  const [page, setPage] = useState(1);

  usePageMeta({
    title: info
      ? `${info.name} - 日韩无码视频高清免费在线观看 -无码中文字幕`
      : '分类不存在 - 无码中文字幕',
    description: info
      ? `${info.intro} 无码中文字幕为你整理${info.name}相关日韩自拍视频，支持地区、视角与年份多维筛选。`
      : '该分类不存在或已下线，可返回首页浏览其他日韩自拍视频分类。',
    keywords: info
      ? `${info.name},日韩自拍视频,${info.name}在线观看,无码中文字幕`
      : '无码中文字幕,分类不存在',
  });

  const result = useMemo(() => {
    if (!info) return [];
    const filtered = filterVideos({
      category: info.name,
      region,
      perspective,
      year,
    });
    return sortVideos(filtered, sort);
  }, [info, region, perspective, year, sort]);

  const totalPages = Math.max(1, Math.ceil(result.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = result.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const activeCount = [region, perspective, year].filter(Boolean).length;

  const groups: FilterGroup[] = [
    { key: 'region', label: '地区', options: regions, value: region },
    { key: 'perspective', label: '视角', options: perspectives, value: perspective },
    { key: 'year', label: '年份', options: years.map(String), value: year },
  ];

  const handleFilterChange = (key: string, value: string) => {
    if (key === 'region') setRegion(value);
    if (key === 'perspective') setPerspective(value);
    if (key === 'year') setYear(value);
    setPage(1);
  };

  const resetFilters = () => {
    setRegion('');
    setPerspective('');
    setYear('');
    setPage(1);
  };

  if (!info) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-folder-3-line text-2xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">分类不存在或已下线</h1>
        <p className="mt-3 max-w-[420px] text-[13px] leading-6 text-foreground-500">
          你访问的分类可能已经调整。可以回到首页重新选择题材，或直接浏览全部内容。
        </p>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          <Link
            to="/"
            className="flex h-10 items-center justify-center whitespace-nowrap rounded-md bg-primary-600 px-5 text-[13px] text-background-50 transition-colors hover:bg-primary-700"
          >
            返回首页
          </Link>
          <Link
            to="/search"
            className="flex h-10 items-center justify-center whitespace-nowrap rounded-md border border-background-300 px-5 text-[13px] text-foreground-700 transition-colors hover:bg-background-100"
          >
            浏览全部内容
          </Link>
        </div>
      </div>
    );
  }

  const relatedTags = Array.from(
    new Set(
      filterVideos({ category: info.name }).flatMap((item) => item.tags),
    ),
  ).slice(0, 8);

  return (
    <div className="w-full">
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-12">
          <nav className="flex flex-wrap items-center gap-1.5 text-[11px] text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="transition-colors hover:text-primary-700">
              首页
            </Link>
            <span className="flex h-3 w-3 items-center justify-center text-background-400">
              <i className="ri-arrow-right-s-line text-xs" />
            </span>
            <span>分类</span>
            <span className="flex h-3 w-3 items-center justify-center text-background-400">
              <i className="ri-arrow-right-s-line text-xs" />
            </span>
            <span className="text-foreground-800">{info.name}</span>
          </nav>

          <div className="mt-5 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-600 text-background-50">
                  <i className={`${info.icon} text-xl`} />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-accent-700">{info.accent}</p>
                  <h1 className="mt-0.5 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
                    {info.name}
                  </h1>
                </div>
              </div>
              <p className="mt-4 max-w-[640px] text-[13px] leading-7 text-foreground-600">{info.intro}</p>
            </div>

            <div className="flex shrink-0 items-center gap-4 rounded-lg border border-background-200 bg-background-50 px-5 py-3">
              <div>
                <p className="font-heading text-xl font-semibold text-primary-700">{result.length}</p>
                <p className="text-[11px] text-foreground-500">符合条件条目</p>
              </div>
              <span className="h-8 w-px bg-background-200" />
              <div>
                <p className="font-heading text-xl font-semibold text-foreground-900">
                  {filterVideos({ category: info.name }).length}
                </p>
                <p className="text-[11px] text-foreground-500">分类总数</p>
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-foreground-400">相关标签</span>
            {relatedTags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                #{tag}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
        <FilterBar groups={groups} onChange={handleFilterChange} onReset={resetFilters} activeCount={activeCount} />

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[12px] text-foreground-500">
            共 <span className="font-medium text-foreground-900">{result.length}</span> 条内容 · 第 {currentPage} / {totalPages} 页
          </p>
          <SortTabs
            value={sort}
            onChange={(value) => {
              setSort(value);
              setPage(1);
            }}
          />
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-5 grid grid-cols-1 gap-x-3 gap-y-6 sm:grid-cols-2 md:gap-x-5 md:gap-y-8 lg:grid-cols-3">
              {pageItems.map((video, index) => (
                <VideoCard key={video.id} video={video} index={index} showTags />
              ))}
            </div>
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-foreground-400">
              <i className="ri-inbox-line text-xl" />
            </span>
            <h2 className="mt-4 text-base font-medium text-foreground-900">当前筛选组合下暂无内容</h2>
            <p className="mt-2 max-w-[380px] text-[12px] leading-6 text-foreground-500">
              试着放宽地区、视角或年份条件，也可以重置筛选后浏览该分类的全部内容。
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={resetFilters}
                className="flex h-10 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 text-[13px] text-background-50 transition-colors hover:bg-primary-700"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-refresh-line text-sm" />
                </span>
                重置筛选
              </button>
              <Link
                to="/"
                className="flex h-10 items-center justify-center whitespace-nowrap rounded-md border border-background-300 px-5 text-[13px] text-foreground-700 transition-colors hover:bg-background-50"
              >
                返回首页
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}